function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { useDrag } from "@use-gesture/react";
import styled, { css } from "styled-components";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import clamp from "astrid-helpers/src/clamp";
import map from "astrid-helpers/src/map";
import snap from "astrid-helpers/src/snap";
import useTemp from "astrid-hooks/src/useTemp";
import { secondaryBackground } from "../../../helpers/background";
import { clipColor, defaultColor, dividerColor, primaryColor } from "../../../helpers/color";
import shadow from "../../../helpers/shadow";
import ClipContext from "./ClipContext";
import Trim from "./Trim";
import Volume from "./Volume";
import { WaveRoot } from "./Wave";
import { getOffset, setOffset } from "./state/offset";
import { getRect } from "./state/rect";
import { getRipple, getShouldRipple, setRipple, useRipple } from "./state/ripple";
import { useScope } from "./state/scope";
import { getSize } from "./state/size";
import { getStep } from "./state/step";
const dragEdgeSpeed = 3;
const dragEdgeThreshold = 100;
const Root = /*#__PURE__*/styled.div.withConfig({
  displayName: "Root",
  componentId: "sc-1vfhkab-0"
})(["position:absolute;display:flex;flex-direction:column;top:0;bottom:0;left:0;color:", ";box-shadow:0 0 0 2px ", ";background-color:", ";transition:0.1s ease-in-out;transition-property:box-shadow;will-change:transform;", " ", " ", " ", ""], defaultColor, dividerColor, clipColor, ({
  active
}) => active && css(["background-color:", "99;"], clipColor), ({
  selected
}) => selected && css(["color:", ";cursor:pointer;", "{background-color:", ";cursor:grab;&:active{cursor:grabbing;}}"], primaryColor, Footer, clipColor), ({
  lifting
}) => lifting && css(["", " z-index:2;background-color:", "99;transform:translateY(-15px);", "{top:15px;bottom:-15px;clip-path:inset(0 0 50%);}"], shadow, clipColor, WaveRoot), ({
  disabled
}) => disabled && css(["", " ", "{cursor:default;}"], secondaryBackground, Footer));
const Content = /*#__PURE__*/styled.div.withConfig({
  displayName: "Content",
  componentId: "sc-1vfhkab-1"
})(["overflow:hidden;position:relative;flex:1;"]);
const Footer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Footer",
  componentId: "sc-1vfhkab-2"
})(["", ";position:relative;overflow:hidden;display:flex;align-items:center;border-top:2px solid ", ";font-size:12px;line-height:30px;& > *:first-child{min-width:14px;}& > * + *{margin-left:5px;}"], secondaryBackground, dividerColor);
const Clip = /*#__PURE__*/memo(({
  end,
  info,
  start,
  footer,
  length,
  active,
  volume,
  fadein,
  fadeout,
  position,
  selected,
  disabled,
  children,
  transparent,
  prevClipEnd,
  nextClipStart,
  disableRipple,
  onMove,
  onSelect,
  onTrimEnd,
  onTrimStart,
  onChangeVolume,
  onChangeFadein,
  onChangeFadeout,
  onChangePosition
}) => {
  end = end || length || 0;
  const ripple = useRipple();
  const {
    end: scopeEnd,
    start: scopeStart,
    length: scopeLength
  } = useScope();
  const moving = useRef(false);
  const [trimming, setTrimming] = useState(false);
  const [tempEnd, setTempEnd, getTempEnd] = useTemp(end);
  const [tempStart, setTempStart, getTempStart] = useTemp(start);
  const [tempFadein, setTempFadein, getTempFadein] = useTemp(fadein);
  const [tempFadeout, setTempFadeout, getTempFadeout] = useTemp(fadeout);
  const [tempPosition, setTempPosition, getTempPosition] = useTemp(position);
  const left = tempPosition - scopeStart;
  const maxEnd = tempPosition - tempStart + tempEnd;
  const right = scopeEnd - maxEnd;
  const displayLength = Math.max(0, Math.min(scopeEnd - scopeStart, tempEnd - tempStart, maxEnd - scopeStart, scopeEnd - tempPosition));
  const onReleaseStart = useCallback(() => onTrimStart && onTrimStart(getTempPosition(), getTempStart(), getTempFadein()), [getTempFadein, getTempPosition, getTempStart, onTrimStart]);
  const onReleaseEnd = useCallback(() => onTrimEnd && onTrimEnd(getTempEnd(), getTempFadeout()), [getTempEnd, getTempFadeout, onTrimEnd]);
  const onChangeStart = useCallback(tempStart => {
    tempStart = tempStart > prevClipEnd ? snap(tempStart, getStep()) : prevClipEnd;
    setTempStart(tempStart);
    setTempPosition(position + tempStart - start);
    if (fadein > 100) {
      setTempFadein(clamp(fadein - (tempStart - start), 100, fadein));
    }
  }, [fadein, prevClipEnd, position, setTempFadein, setTempPosition, setTempStart, start]);
  const onChangeEnd = useCallback(tempEnd => {
    tempEnd = tempEnd < nextClipStart ? snap(tempEnd, getStep()) : nextClipStart;
    setTempEnd(tempEnd);
    if (fadeout > 100) {
      setTempFadeout(clamp(fadeout - (end - tempEnd), 100, fadeout));
    }
  }, [end, fadeout, nextClipStart, setTempEnd, setTempFadeout]);
  const onDrag = useDrag(useCallback(({
    xy,
    last,
    first,
    event,
    movement
  }) => {
    event.stopPropagation();
    if (selected && !first) {
      if (last) {
        const ripple = getRipple();
        setRipple(false);
        if (moving.current) {
          onChangePosition && onChangePosition(getTempPosition(), ripple[2]);
          moving.current = false;
        }
      } else {
        const [x, y] = movement;
        const size = getSize();
        const offset = getOffset();
        const {
          width
        } = getRect();
        const deltaX = map(x, 0, width, 0, size);
        if (!moving.current) {
          moving.current = true;
          onMove && onMove();
          console.log("move");
        }
        if (xy[0] <= dragEdgeThreshold) {
          setOffset(Math.max(offset - (dragEdgeThreshold + xy[0]) * dragEdgeSpeed, 0));
        }
        if (width - xy[0] <= dragEdgeThreshold) {
          setOffset(Math.max(offset + (dragEdgeThreshold + width - xy[0]) * dragEdgeSpeed, 0));
        }
        const minX = prevClipEnd - start;
        const lifting = y < -15;
        let delta = lifting || deltaX > minX ? deltaX : minX;
        delta = position + delta < 0 ? -position : delta;
        if (!disableRipple) {
          const range = [position, position + end - start];
          setRipple([lifting, range, Math.max(minX - end + start, delta)]);
        }
        setTempPosition(position + delta);
      }
    }
  }, [disableRipple, end, getTempPosition, onChangePosition, onMove, position, prevClipEnd, selected, setTempPosition, start]));
  const selectClip = () => {
    if (!selected) {
      onSelect(true);
    }
  };
  useEffect(() => {
    const shouldRipple = getShouldRipple();
    if (shouldRipple && ripple && !moving.current) {
      if (position > ripple[1][0]) {
        setTempPosition(position + ripple[2]);
      } else {
        setTempPosition(position);
      }
    }
  }, [position, ripple, setTempPosition]);
  useEffect(() => {
    if (!active && end !== tempEnd || start !== tempStart) {
      if (!trimming) {
        setTrimming(true);
        onMove && onMove();
      }
    } else if (trimming) {
      setTrimming(false);
      onMove && onMove();
    }
  }, [active, end, onMove, start, tempEnd, tempStart, trimming]);
  const lifting = moving.current && ripple && ripple[0];
  const showVolume = selected && !lifting;
  const context = useMemo(() => ({
    volume,
    length,
    selected,
    trimming,
    end: tempEnd + Math.min(0, right),
    start: tempStart - Math.min(0, left)
  }), [left, length, right, selected, tempEnd, tempStart, trimming, volume]);
  if (displayLength <= 0) return false;
  const style = {
    left: `${Math.max(0, left / scopeLength) * 100}%`,
    width: `${displayLength / scopeLength * 100}%`
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Root, {
    style: style,
    active: active,
    lifting: lifting,
    selected: selected,
    disabled: disabled,
    transparent: transparent,
    onMouseDown: selectClip
  }, /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(ClipContext.Provider, {
    value: context
  }, children), info && showVolume && !trimming && /*#__PURE__*/React.createElement(Volume, _extends({}, context, {
    fadein: tempFadein,
    fadeout: tempFadeout,
    onChangeVolume: onChangeVolume,
    onChangeFadein: onChangeFadein,
    onChangeFadeout: onChangeFadeout
  }))), info && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Footer, onDrag(), footer), selected && /*#__PURE__*/React.createElement(Trim, {
    end: end,
    start: start,
    length: length,
    onChangeEnd: onChangeEnd,
    onReleaseEnd: onReleaseEnd,
    onChangeStart: onChangeStart,
    onReleaseStart: onReleaseStart
  }))));
});
Clip.defaultProps = {
  end: 0,
  start: 0,
  length: 0,
  volume: 1,
  fadein: 0,
  fadeout: 0,
  prevClipEnd: -Infinity,
  nextClipStart: Infinity
};
export default Clip;
//# sourceMappingURL=Clip.js.map